import { useMemo, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  GitBranch,
  ListChecks,
  Minus,
  X,
} from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { NodeStruct } from '@/types/node-types';
import {
  SWITCH_NODE_ID,
  getSelectedCase,
  isBranchingStruct,
  IF_NODE_ID,
} from '@/pages/workflow/helper/functional-control-flow';
import {
  decisionBecauseClause,
  describeLeafCondition,
  describeLeafSeen,
  getClassifySummary,
  getSwitchDecision,
  type DecisionCase,
  type SwitchDecision,
} from '@/pages/workflow/helper/decision-trace';
import { useResolvedSkip } from './skipped-branch-chip';
import { cn } from '@/lib/utils';

export const CLASSIFY_DECIDE_NODE_ID = 'classify_decide_node';

/**
 * Which logic nodes get a "Why" card in the config panel after a run.
 * Validate (checks list), Repeat Until (timeline), Union / Reduce (counts)
 * and Jev (probabilities) already explain themselves in their own cards.
 */
export const WHY_CARD_NODE_IDS = new Set<string>([
  SWITCH_NODE_ID,
  IF_NODE_ID, // --- if/else (Phase 8b): same trace, same card ---
  CLASSIFY_DECIDE_NODE_ID,
]);

const Outcome = ({ value }: { value: boolean | null }) =>
  value === null ? (
    <Minus
      className='size-3.5 text-muted-foreground'
      aria-label='not checked'
    />
  ) : value ? (
    <Check className='size-3.5 text-emerald-600' aria-label='yes' />
  ) : (
    <X className='size-3.5 text-rose-600' aria-label='no' />
  );

const caseOutcomeWords = (entry: DecisionCase): string =>
  entry.matched === null
    ? 'not checked'
    : entry.matched
      ? 'matched'
      : 'did not match';

function SwitchWhy({
  decision,
  fallbackLabel,
}: {
  decision: SwitchDecision | null;
  fallbackLabel: string | null;
}) {
  const [showRaw, setShowRaw] = useState(false);
  if (!decision) {
    if (!fallbackLabel) return null;
    return (
      <p className='text-xs text-muted-foreground'>
        Took «{fallbackLabel}». This run predates the decision trace, so the
        values the Switch saw were not recorded; run again to see why.
      </p>
    );
  }
  const label = decision.selected_label ?? fallbackLabel;
  const because = decisionBecauseClause(decision);
  return (
    <div className='space-y-2'>
      <p className='text-sm' data-testid='why-switch-sentence'>
        {label ? (
          <>
            Took <span className='font-semibold'>«{label}»</span>
            {because ? ` because ${because}.` : '.'}
          </>
        ) : (
          <>
            The {decision.branches ? 'If' : 'Switch'} stopped before choosing a
            branch: {because}.
          </>
        )}
        {decision.is_default &&
          decision.reason !== 'null_default' &&
          // --- if/else (Phase 8b): an If's fallback is "otherwise", not "default"
          (decision.branch ? (
            <span className='text-muted-foreground'>
              {' '}
              (the Otherwise branch)
            </span>
          ) : (
            <span className='text-muted-foreground'> (the default branch)</span>
          ))}
      </p>
      <ol className='space-y-1.5' data-testid='why-switch-cases'>
        {decision.evaluated.map((entry) => (
          <li
            key={entry.case_index}
            className={cn(
              'rounded-md border px-2 py-1.5 text-xs',
              entry.matched === true
                ? 'border-amber-300 bg-amber-50/70 dark:bg-amber-950/20'
                : entry.matched === false
                  ? 'border-border'
                  : 'border-dashed border-border text-muted-foreground',
            )}
            data-testid={`why-switch-case-${entry.case_index}`}
            data-matched={String(entry.matched)}
          >
            <div className='flex items-center gap-1.5'>
              <span className='text-muted-foreground'>
                {entry.case_index + 1}.
              </span>
              <span className='font-medium'>{entry.label}</span>
              <span className='ml-auto text-muted-foreground'>
                {caseOutcomeWords(entry)}
              </span>
            </div>
            {entry.conditions.length > 0 && (
              <ul className='mt-1 space-y-0.5 pl-4'>
                {entry.conditions.map((leaf, index) => (
                  <li key={index} className='flex items-start gap-1.5'>
                    <span className='mt-0.5 shrink-0'>
                      <Outcome value={leaf.result} />
                    </span>
                    <span className='min-w-0'>
                      <span>{describeLeafCondition(leaf)}</span>
                      <span className='text-muted-foreground'>
                        {' '}
                        · {describeLeafSeen(leaf)}
                      </span>
                    </span>
                  </li>
                ))}
                {Boolean(entry.conditions.length > 1 && entry.combine) && (
                  <li className='text-[10px] text-muted-foreground'>
                    {entry.combine === 'and'
                      ? 'all of these had to hold'
                      : 'any one of these was enough'}
                  </li>
                )}
              </ul>
            )}
          </li>
        ))}
      </ol>
      {decision.truncated && (
        <p className='text-[11px] text-muted-foreground'>
          Some values were shortened to keep the trace small.
        </p>
      )}
      <button
        type='button'
        className='flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground'
        onClick={() => setShowRaw((open) => !open)}
        data-testid='why-show-raw'
      >
        {showRaw ? (
          <ChevronDown className='size-3' />
        ) : (
          <ChevronRight className='size-3' />
        )}
        {showRaw ? 'Hide raw trace' : 'Show raw trace'}
      </button>
      {showRaw && (
        <pre className='max-h-64 overflow-auto rounded-md bg-muted p-2 text-[10px] leading-snug'>
          {JSON.stringify(decision, null, 2)}
        </pre>
      )}
    </div>
  );
}

function ClassifyWhy({ result }: { result: unknown }) {
  const summary = useMemo(() => getClassifySummary(result), [result]);
  if (!summary) return null;
  const percent =
    summary.confidence === null
      ? null
      : Math.round(Math.max(0, Math.min(1, summary.confidence)) * 100);
  return (
    <div className='space-y-2' data-testid='why-classify'>
      <p className='text-sm'>
        Decided <span className='font-semibold'>«{summary.label}»</span>
        {percent !== null ? ` with ${percent}% confidence` : ''}.
      </p>
      {percent !== null && (
        <div
          className='h-1.5 w-full overflow-hidden rounded-full bg-muted'
          role='progressbar'
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label='confidence'
        >
          <div
            className='h-full rounded-full bg-emerald-500'
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {summary.reasonCodes.length > 0 && (
        <div className='flex flex-wrap gap-1'>
          {summary.reasonCodes.map((code) => (
            <span
              key={code}
              className='rounded-full border px-2 py-0.5 font-mono text-[10px]'
            >
              {code}
            </span>
          ))}
        </div>
      )}
      {summary.rationale ? (
        <p className='text-xs text-muted-foreground'>{summary.rationale}</p>
      ) : null}
    </div>
  );
}

/** The Classify / Decide node's one-line chip on the canvas: «label» · 82%. */
export function ClassifyDecisionChip({ result }: { result: unknown }) {
  const summary = useMemo(() => getClassifySummary(result), [result]);
  if (!summary) return null;
  const percent =
    summary.confidence === null
      ? null
      : Math.round(Math.max(0, Math.min(1, summary.confidence)) * 100);
  return (
    <div
      className='mx-3 mb-1 flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50/80 px-2 py-1 text-[11px] text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200'
      data-testid='classify-decision-chip'
      title={summary.rationale ?? undefined}
    >
      <ListChecks className='size-3 shrink-0' />
      <span className='truncate'>
        <span className='font-medium'>«{summary.label}»</span>
        {percent !== null ? ` · ${percent}%` : ''}
      </span>
    </div>
  );
}

/**
 * "Why" card: after a run, what this logic node decided and why, in words.
 * Mounted at the top of the Configure tab for Switch / Match and
 * Classify / Decide, and for any node a Switch skipped.
 */
export function DecisionWhyCard({
  nodeId,
  struct,
}: {
  nodeId: string;
  struct: NodeStruct;
}) {
  const result = useNodeStore(
    (state) =>
      (
        state.nodes.find((node) => node.id === nodeId)?.data as
          | { result?: unknown }
          | undefined
      )?.result,
  );
  const skip = useResolvedSkip(nodeId, result);
  // --- if/else (Phase 8b): an If carries the same trace as a Switch ---
  const branching = isBranchingStruct(struct);
  // --- end if/else ---
  const decision = useMemo(
    () => (branching ? getSwitchDecision(result) : null),
    [branching, result],
  );
  const fallbackLabel = branching ? getSelectedCase(result) : null;
  const isClassify = struct.node_id === CLASSIFY_DECIDE_NODE_ID;
  const classify = useMemo(
    () => (isClassify ? getClassifySummary(result) : null),
    [isClassify, result],
  );

  if (!skip && !decision && !fallbackLabel && !classify) return null;

  return (
    <section
      className='mb-4 rounded-lg border bg-muted/30 p-3'
      data-testid='decision-why-card'
      aria-label='Why'
    >
      <div className='mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
        {skip ? (
          <CornerDownRight className='size-3.5' />
        ) : isClassify ? (
          <ListChecks className='size-3.5' />
        ) : (
          <GitBranch className='size-3.5' />
        )}
        <span>Why</span>
        <span className='ml-auto font-normal normal-case'>last run</span>
      </div>
      {skip ? (
        <p className='text-sm' data-testid='why-skipped-sentence'>
          This node did not run: {skip.sentence.replace(/^skipped: /, '')}.
        </p>
      ) : isClassify ? (
        <ClassifyWhy result={result} />
      ) : (
        <SwitchWhy decision={decision} fallbackLabel={fallbackLabel} />
      )}
    </section>
  );
}
